import React, { useEffect, useRef, useState } from "react";
import { FaGithub, FaCloudUploadAlt, FaCheck, FaArrowRight, FaBitbucket, FaGitlab, FaAws } from "react-icons/fa";
import { VscAzureDevops } from "react-icons/vsc";
import { zipTypeExtensions } from "../../settings/shared/constants";
import { showNotificationGlobal } from "../common/NotificationProvider";

const IMPORT_SOURCES = {
  GITHUB: "GITHUB",
  UPLOAD: "UPLOAD",
  BITBUCKET: "BITBUCKET",
  GITLAB: "GITLAB",
  AZURE_DEVOPS: "AZURE_DEVOPS",
  CODECOMMIT: "CODECOMMIT",
  S3: "S3",
};

const SOURCE_CARDS = [
  {
    id: IMPORT_SOURCES.GITHUB,
    title: "GitHub repository",
    description: "Paste a repo URL. We clone it read-only and run analysis.",
    bullets: ["Public or connected GitHub URL", "Read-only clone", "No training on your code"],
    icon: FaGithub,
    urlPlaceholder: "https://github.com/org/repo",
    urlExample: "https://github.com/superagentx/demo-app",
    urlPattern: /^https?:\/\/(www\.)?github\.com\/[\w.-]+\/[\w.-]+/i,
  },
  {
    id: IMPORT_SOURCES.UPLOAD,
    title: "Upload archive",
    description: "Drop a ZIP or TAR of the codebase. Secrets are scanned first.",
    bullets: ["ZIP, TAR, TAR.GZ, RAR", "Secrets scanning before analysis", "Works for large codebases"],
    icon: FaCloudUploadAlt,
  },
  {
    id: IMPORT_SOURCES.BITBUCKET,
    title: "Bitbucket",
    description: "Import a Bitbucket Cloud or Server repository.",
    bullets: ["Workspace and repo URL", "Read-only clone"],
    icon: FaBitbucket,
    comingSoon: true,
    urlPlaceholder: "https://bitbucket.org/workspace/repo",
    urlExample: "https://bitbucket.org/workspace/demo-app",
    urlPattern: /^https?:\/\/(www\.)?bitbucket\.org\/[\w.-]+\/[\w.-]+/i,
  },
  {
    id: IMPORT_SOURCES.GITLAB,
    title: "GitLab",
    description: "Import a GitLab.com or self-hosted project.",
    bullets: ["Group and project URL", "Read-only clone"],
    icon: FaGitlab,
    comingSoon: true,
    urlPlaceholder: "https://gitlab.com/group/project",
    urlExample: "https://gitlab.com/group/demo-app",
    urlPattern: /^https?:\/\/.+/i,
  },
  {
    id: IMPORT_SOURCES.AZURE_DEVOPS,
    title: "Azure DevOps Repos",
    description: "Import an Azure Repos Git repository.",
    bullets: ["Organization and project", "Read-only clone"],
    icon: VscAzureDevops,
    comingSoon: true,
    urlPlaceholder: "https://dev.azure.com/org/project/_git/repo",
    urlExample: "https://dev.azure.com/org/project/_git/demo-app",
    urlPattern: /^https?:\/\/(dev\.azure\.com|[\w.-]+\.visualstudio\.com)\//i,
  },
  {
    id: IMPORT_SOURCES.CODECOMMIT,
    title: "AWS CodeCommit",
    description: "Import a CodeCommit repository from your AWS account.",
    bullets: ["Region and repository name", "Read-only clone"],
    icon: FaAws,
    comingSoon: true,
    urlPlaceholder: "https://git-codecommit.us-east-1.amazonaws.com/v1/repos/repo",
    urlExample: "https://git-codecommit.us-east-1.amazonaws.com/v1/repos/demo-app",
    urlPattern: /^https?:\/\/git-codecommit\.[\w-]+\.amazonaws\.com\//i,
  },
  {
    id: IMPORT_SOURCES.S3,
    title: "S3 / Object Storage",
    description: "Import a codebase archive from an S3-compatible bucket.",
    bullets: ["Bucket and object prefix", "ZIP or TAR archive"],
    icon: FaAws,
    comingSoon: true,
    urlLabel: "Object URL",
    urlPlaceholder: "s3://bucket/path/to/archive.zip",
    urlExample: "s3://my-bucket/apps/demo-app.zip",
    urlPattern: /^(s3:\/\/|https?:\/\/).+/i,
  },
];


const CORE_ICONS = [
  "fa-brands fa-github",
  "fa-solid fa-folder-tree",
  "fa-solid fa-robot",
  "fa-solid fa-gauge-high",
];

const ACTIVITY_LOGS = [
  ["Resolving repository access", "Fetching default branch", "Streaming source objects"],
  ["Walking the file tree", "Detecting languages", "Extracting symbols and imports"],
  ["Security reviewer running", "Architecture reviewer running", "Grouping findings by severity"],
  ["Scoring application health", "Assembling charts", "Opening the insights workspace"],
];

const STATUS_LINES = [
  [
    { title: "Connecting to GitHub", sub: "Checking repository access" },
    { title: "Cloning the repository", sub: "Pulling source files and history" },
    { title: "Unpacking the codebase", sub: "Getting files ready for analysis" },
  ],
  [
    { title: "Indexing source files", sub: "Mapping languages and structure" },
    { title: "Discovering architecture", sub: "Finding modules and entry points" },
    { title: "Building the symbol graph", sub: "Linking calls and imports" },
  ],
  [
    { title: "Running AI reviewers", sub: "Looking for findings and risks" },
    { title: "Analyzing patterns", sub: "Reviewing APIs and dependencies" },
    { title: "Scoring code health", sub: "Grouping issues by severity" },
  ],
  [
    { title: "Preparing your insights", sub: "Assembling the dashboard" },
    { title: "Almost ready", sub: "Putting the last pieces in place" },
    { title: "Opening results", sub: "Loading the review workspace" },
  ],
];

const IMPORT_CSS = `
  .sa-import-wrap {
    width: 100%;
    margin: 0;
    font-family: 'DM Sans', system-ui, sans-serif;
    text-align: left;
  }
  .sa-import-lead {
    margin: 0;
    font-size: 14px;
    color: #64748b;
    line-height: 1.5;
    max-width: 560px;
  }
  .sa-import-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin: 0 0 20px;
    flex-wrap: wrap;
  }
  .sa-import-section-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #94a3b8;
    margin: 0 0 10px;
  }
  .sa-import-soon-group {
    margin-top: 22px;
  }
  .sa-import-cards {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 20px;
    align-items: stretch;
  }
  .sa-import-cards.is-ready {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-width: none;
  }
  @media (max-width: 980px) {
    .sa-import-cards { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 720px) {
    .sa-import-cards { grid-template-columns: 1fr; }
  }
  .sa-import-card {
    border: 1.5px solid #e2e8f0;
    border-radius: 16px;
    background: #fff;
    padding: 22px 22px 16px;
    cursor: pointer;
    text-align: left;
    display: flex;
    flex-direction: column;
    outline: none;
    transition: border-color 0.18s, background 0.18s, transform 0.18s, box-shadow 0.18s;
    box-shadow: none;
  }
  .sa-import-card:hover,
  .sa-import-card:focus-visible {
    border-color: rgba(61, 145, 228, 0.55);
    background: #f8fbff;
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
  }
  .sa-import-card.is-selected {
    border-color: var(--activemenu, #3d91e4);
    background: #f8fbff;
    box-shadow: 0 8px 22px rgba(61, 145, 228, 0.12);
  }
  .sa-import-card.is-soon {
    background: #fafbfc;
    opacity: 0.88;
    cursor: default;
  }
  .sa-import-card.is-soon:hover,
  .sa-import-card.is-soon:focus-visible {
    transform: none;
    box-shadow: none;
    border-color: #e2e8f0;
    background: #fafbfc;
  }
  .sa-import-card-top {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 10px;
  }
  .sa-import-card-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(61, 145, 228, 0.08);
    color: var(--active-color, #033b70);
    font-size: 22px;
    flex-shrink: 0;
    transition: background 0.18s, color 0.18s;
  }
  .sa-import-card:hover .sa-import-card-icon,
  .sa-import-card:focus-visible .sa-import-card-icon,
  .sa-import-card.is-selected .sa-import-card-icon {
    background: var(--activemenu, #3d91e4);
    color: #fff;
  }
  .sa-import-card.is-soon:hover .sa-import-card-icon,
  .sa-import-card.is-soon:focus-visible .sa-import-card-icon {
    background: rgba(61, 145, 228, 0.08);
    color: var(--active-color, #033b70);
  }
  .sa-import-card-check {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 1.5px solid #cbd5e1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    color: #fff;
    font-size: 10px;
    flex-shrink: 0;
  }
  .sa-import-card.is-selected .sa-import-card-check {
    border-color: var(--activemenu, #3d91e4);
    background: var(--activemenu, #3d91e4);
  }
  .sa-import-card h3 {
    font-size: 1.05rem;
    font-weight: 750;
    color: #0f172a;
    margin: 0 0 4px;
    letter-spacing: -0.02em;
  }
  .sa-import-card > p,
  .sa-import-card-copy p {
    font-size: 13.5px;
    color: #64748b;
    margin: 0;
    line-height: 1.45;
  }
  .sa-import-card-copy { min-width: 0; flex: 1; }
  .sa-import-footer-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .sa-import-next {
    border: none;
    border-radius: 999px;
    background: var(--activemenu, #3d91e4);
    color: #fff;
    font-weight: 650;
    font-size: 13.5px;
    padding: 11px 18px 11px 20px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-family: inherit;
    box-shadow: 0 8px 20px rgba(61, 145, 228, 0.28);
    transition: transform 0.15s, box-shadow 0.15s, filter 0.15s;
  }
  .sa-import-next:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.04);
    box-shadow: 0 10px 24px rgba(61, 145, 228, 0.36);
  }
  .sa-import-next:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }
  .sa-import-next svg { font-size: 12px; }
  .sa-import-soon-badge {
    display: inline-flex;
    align-items: center;
    margin-top: 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b45309;
    background: #fef3c7;
    border-radius: 999px;
    padding: 2px 8px;
  }
  .sa-import-soon {
    text-align: center;
    padding: 36px 24px;
  }
  .sa-import-soon h3 {
    margin: 0 0 8px;
    font-size: 1.15rem;
    font-weight: 750;
    color: #0f172a;
  }
  .sa-import-soon p {
    margin: 0;
    font-size: 14px;
    color: #64748b;
    line-height: 1.5;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
  }
  .sa-import-source-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    background: #f8fbff;
    color: #0f172a;
    font-size: 13px;
    font-weight: 650;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 0.15s, background 0.15s;
  }
  .sa-import-source-chip:hover:not(:disabled) {
    border-color: var(--activemenu, #3d91e4);
    background: #f0f7ff;
  }
  .sa-import-source-chip:disabled {
    cursor: default;
    opacity: 0.7;
  }
  .sa-import-source-chip svg { font-size: 16px; color: var(--activemenu, #3d91e4); }
  .sa-import-source-chip .sa-import-change {
    margin-left: 4px;
    font-size: 12px;
    font-weight: 600;
    color: var(--activemenu, #3d91e4);
  }
  .sa-import-card ul {
    list-style: none;
    padding: 0;
    margin: 14px 0 0;
  }
  .sa-import-card li {
    font-size: 13px;
    color: #475569;
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-bottom: 7px;
    line-height: 1.35;
  }
  .sa-import-card li i {
    color: var(--activemenu, #3d91e4);
    margin-top: 3px;
    font-size: 10px;
  }
  .sa-import-stage {
    display: grid;
    grid-template-columns: minmax(260px, 360px) minmax(0, 1fr);
    gap: 28px 32px;
    align-items: stretch;
    width: 100%;
  }
  .sa-import-stage-aside {
    min-width: 0;
    background: linear-gradient(180deg, #f8fbff 0%, #f8fafc 100%);
    border: 1px solid #e8eef6;
    border-radius: 16px;
    padding: 22px 20px 18px;
    animation: sa-import-aside-in 0.5s ease both;
  }
  .sa-import-aside-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 6px;
  }
  .sa-import-aside-head h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 750;
    color: #0f172a;
    letter-spacing: -0.02em;
  }
  .sa-import-aside-badge {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #15803d;
    background: #ecfdf3;
    border: 1px solid #bbf7d0;
    border-radius: 999px;
    padding: 3px 8px;
    white-space: nowrap;
  }
  .sa-import-stage-aside > p {
    margin: 0 0 18px;
    font-size: 12.5px;
    color: #64748b;
    line-height: 1.5;
  }
  .sa-import-stage-main {
    min-width: 0;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 24px 26px 20px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
    animation: sa-import-view-in 0.48s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  @keyframes sa-import-aside-in {
    from { opacity: 0; transform: translateX(-16px); }
    to { opacity: 1; transform: none; }
  }
  @keyframes sa-import-view-in {
    from { opacity: 0; transform: translateX(36px) scale(0.97); }
    to { opacity: 1; transform: none; }
  }
  .sa-import-steps {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .sa-import-steps li {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    position: relative;
    padding: 0 0 16px;
    animation: sa-import-step-in 0.45s ease both;
  }
  .sa-import-steps li:last-child { padding-bottom: 0; }
  .sa-import-steps li:nth-child(1) { animation-delay: 0.08s; }
  .sa-import-steps li:nth-child(2) { animation-delay: 0.16s; }
  .sa-import-steps li:nth-child(3) { animation-delay: 0.24s; }
  .sa-import-steps li:nth-child(4) { animation-delay: 0.32s; }
  .sa-import-steps li:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 13px;
    top: 30px;
    bottom: 2px;
    width: 2px;
    background: linear-gradient(180deg, #bfdbfe, #e2e8f0);
    border-radius: 2px;
  }
  .sa-import-step-mark {
    width: 28px;
    height: 28px;
    border-radius: 9px;
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 11px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    z-index: 1;
  }
  .sa-import-steps li.is-next .sa-import-step-mark {
    background: var(--activemenu, #3d91e4);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 0 0 4px rgba(61, 145, 228, 0.14);
  }
  .sa-import-step-body { min-width: 0; padding-top: 3px; }
  .sa-import-steps strong {
    display: block;
    font-size: 13px;
    font-weight: 650;
    color: #0f172a;
  }
  .sa-import-steps li.is-next strong { color: var(--active-color, #033b70); }
  .sa-import-steps span {
    display: block;
    font-size: 12px;
    color: #64748b;
    line-height: 1.45;
    margin-top: 2px;
  }
  @keyframes sa-import-step-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: none; }
  }
  @media (prefers-reduced-motion: reduce) {
    .sa-import-stage-aside,
    .sa-import-stage-main,
    .sa-import-steps li {
      animation: none;
    }
  }
  .sa-import-stage-title {
    margin: 0 0 4px;
    font-size: 1.05rem;
    font-weight: 750;
    color: #0f172a;
    letter-spacing: -0.02em;
  }
  .sa-import-stage-copy {
    margin: 0 0 16px;
    font-size: 13px;
    color: #64748b;
    line-height: 1.45;
  }
  .sa-import-source-switch {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 16px;
  }
  .sa-import-source-tab {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #475569;
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 12.5px;
    font-weight: 650;
    font-family: inherit;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
  }
  .sa-import-source-tab svg { font-size: 14px; }
  .sa-import-source-tab:hover:not(:disabled) {
    border-color: color-mix(in srgb, var(--activemenu, #3d91e4) 45%, #e2e8f0);
    color: #0f172a;
  }
  .sa-import-source-tab.is-active {
    border-color: var(--activemenu, #3d91e4);
    background: #f0f7ff;
    color: var(--active-color, #033b70);
  }
  .sa-import-source-tab.is-active svg { color: var(--activemenu, #3d91e4); }
  .sa-import-source-more {
    border: none;
    background: transparent;
    color: var(--activemenu, #3d91e4);
    font-size: 12.5px;
    font-weight: 650;
    font-family: inherit;
    cursor: pointer;
    padding: 6px 8px;
  }
  .sa-import-source-more:hover:not(:disabled) { text-decoration: underline; }
  .sa-import-source-tab:disabled,
  .sa-import-source-more:disabled { opacity: 0.55; cursor: not-allowed; }
  .sa-import-form {
    width: 100%;
    max-width: none;
  }
  .sa-import-panel {
    border: none;
    border-radius: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    box-shadow: none;
  }
  .sa-import-field label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 6px;
  }
  .sa-import-field label .req { color: #ef4444; }
  .sa-import-url-row {
    display: flex;
    align-items: stretch;
    gap: 8px;
  }
  .sa-import-field input {
    flex: 1;
    min-width: 0;
    width: auto;
    height: 38px;
    box-sizing: border-box;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    padding: 7px 12px;
    font-size: 13px;
    line-height: 1.3;
    color: #0f172a;
    outline: none;
    font-family: inherit;
  }
  .sa-import-field input::placeholder {
    color: #94a3b8;
  }
  .sa-import-field input:focus {
    border-color: var(--activemenu, #3d91e4);
    box-shadow: 0 0 0 3px rgba(61, 145, 228, 0.12);
  }
  .sa-import-go {
    position: static;
    transform: none;
    width: auto;
    height: 38px;
    padding: 0 14px;
    border: none;
    border-radius: 8px;
    background: var(--activemenu, #3d91e4);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: pointer;
    box-shadow: none;
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 650;
    white-space: nowrap;
    flex-shrink: 0;
    transition: filter 0.15s, opacity 0.15s;
  }
  .sa-import-go:hover:not(:disabled) {
    transform: none;
    filter: brightness(1.05);
  }
  .sa-import-go:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .sa-import-go svg { font-size: 11px; }
  .sa-import-ready {
    margin-top: 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: #f8fbff;
    flex-wrap: wrap;
  }
  .sa-import-ready strong {
    font-size: 13px;
    color: #0f172a;
    display: block;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sa-import-ready span { font-size: 12px; color: #64748b; }
  .sa-import-ready-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .sa-import-text-btn {
    border: none;
    background: transparent;
    color: #64748b;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    padding: 6px 4px;
    font-family: inherit;
  }
  .sa-import-text-btn:hover:not(:disabled) { color: #0f172a; }
  .sa-import-text-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .sa-import-go-wide {
    border: none;
    border-radius: 8px;
    background: var(--activemenu, #3d91e4);
    color: #fff;
    font-weight: 650;
    font-size: 12.5px;
    padding: 8px 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-family: inherit;
    box-shadow: none;
  }
  .sa-import-go-wide:hover:not(:disabled) { filter: brightness(1.05); }
  .sa-import-go-wide:disabled { opacity: 0.5; cursor: not-allowed; }
  .sa-import-hint {
    margin-top: 6px;
    font-size: 12px;
    color: #94a3b8;
  }
  .sa-import-drop {
    border: 1px dashed rgba(61, 145, 228, 0.45);
    border-radius: 10px;
    background: #f8fbff;
    padding: 20px 14px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.12s, background 0.12s;
  }
  .sa-import-drop.is-dragover {
    border-color: var(--activemenu, #3d91e4);
    background: rgba(61, 145, 228, 0.08);
  }
  .sa-import-drop .icon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(61, 145, 228, 0.12);
    color: var(--activemenu, #3d91e4);
    margin-bottom: 8px;
    font-size: 15px;
  }
  .sa-import-drop-title {
    font-weight: 650;
    font-size: 13px;
    color: #0f172a;
  }
  .sa-import-drop-sub {
    font-size: 12px;
    color: #64748b;
    margin-top: 3px;
  }
  @media (max-width: 860px) {
    .sa-import-stage {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }
  .sa-import-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 18px;
    flex-wrap: wrap;
  }
  .sa-import-secure {
    font-size: 12px;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .sa-import-secure i { color: #16a34a; }

  .sa-work {
    max-width: 520px;
    margin: 0 auto;
    text-align: center;
    padding: 8px 16px 24px;
    position: relative;
  }
  .sa-work-scene {
    position: relative;
    width: 200px;
    height: 200px;
    margin: 0 auto 28px;
  }
  .sa-work-glow {
    position: absolute;
    inset: -18px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(61, 145, 228, 0.22) 0%, transparent 68%);
    animation: sa-work-breathe 2.4s ease-in-out infinite;
  }
  @keyframes sa-work-breathe {
    0%, 100% { transform: scale(0.92); opacity: 0.7; }
    50% { transform: scale(1.08); opacity: 1; }
  }
  .sa-work-ring {
    position: absolute;
    border-radius: 50%;
    border: 2px solid transparent;
  }
  .sa-work-ring.r1 {
    inset: 8px;
    border-top-color: var(--activemenu, #3d91e4);
    border-right-color: rgba(61, 145, 228, 0.25);
    animation: sa-work-spin 1.15s linear infinite;
  }
  .sa-work-ring.r2 {
    inset: 22px;
    border-bottom-color: #60a5fa;
    border-left-color: rgba(96, 165, 250, 0.28);
    animation: sa-work-spin 1.7s linear infinite reverse;
  }
  .sa-work-ring.r3 {
    inset: 36px;
    border: 1.5px dashed rgba(61, 145, 228, 0.28);
    animation: sa-work-spin 8s linear infinite;
  }
  @keyframes sa-work-spin { to { transform: rotate(360deg); } }
  .sa-work-core {
    position: absolute;
    inset: 52px;
    border-radius: 50%;
    background: linear-gradient(180deg, #3d91e4, #2563eb);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    box-shadow: 0 10px 24px rgba(37, 99, 235, 0.35);
    animation: sa-work-core-pulse 1.6s ease-in-out infinite;
  }
  @keyframes sa-work-core-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.06); }
  }
  .sa-work-scan {
    position: absolute;
    left: 18%;
    right: 18%;
    height: 2px;
    top: 18%;
    background: linear-gradient(90deg, transparent, #7dd3fc, transparent);
    animation: sa-work-scan 2.2s ease-in-out infinite;
    opacity: 0.85;
    z-index: 2;
    pointer-events: none;
  }
  @keyframes sa-work-scan {
    0% { top: 18%; opacity: 0; }
    15% { opacity: 1; }
    85% { opacity: 1; }
    100% { top: 78%; opacity: 0; }
  }
  .sa-work-orbit {
    position: absolute;
    inset: 4px;
    animation: sa-work-spin 3.6s linear infinite;
  }
  .sa-work-orbit.rev { animation: sa-work-spin 4.8s linear infinite reverse; }
  .sa-work-pebble {
    position: absolute;
    top: 0;
    left: 50%;
    width: 8px;
    height: 8px;
    margin-left: -4px;
    border-radius: 50%;
    background: #3d91e4;
    box-shadow: 0 0 0 4px rgba(61, 145, 228, 0.16);
  }
  .sa-work-orbit.rev .sa-work-pebble { background: #60a5fa; width: 6px; height: 6px; }
  .sa-work-kicker {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--activemenu, #3d91e4);
    margin-bottom: 8px;
  }
  .sa-work-copy { min-height: 74px; }
  .sa-work-copy.is-out {
    opacity: 0;
    transform: translateY(10px);
    transition: opacity 0.32s ease, transform 0.32s ease;
  }
  .sa-work-copy.is-in {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 0.38s ease, transform 0.38s ease;
  }
  .sa-work-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 750;
    letter-spacing: -0.03em;
    color: #0f172a;
  }
  .sa-work-sub {
    margin: 8px 0 0;
    font-size: 14px;
    color: #64748b;
    line-height: 1.45;
  }
  .sa-work-repo {
    margin: 4px auto 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid #e2e8f0;
    font-size: 13px;
    font-weight: 650;
    color: #334155;
    animation: sa-import-in 0.45s ease;
  }
  @keyframes sa-import-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .sa-work-log {
    margin: 18px auto 0;
    max-width: 440px;
    min-height: 22px;
    font-size: 13px;
    color: #3d91e4;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  .sa-work-log.is-out { opacity: 0; transition: opacity 0.2s ease; }
  .sa-work-log.is-in { opacity: 1; transition: opacity 0.25s ease; }
  .sa-work-log em {
    display: inline-block;
    width: 7px;
    height: 7px;
    margin-right: 8px;
    border-radius: 50%;
    background: #3d91e4;
    animation: sa-work-blink 1s step-end infinite;
    vertical-align: middle;
  }
  @keyframes sa-work-blink { 50% { opacity: 0.15; } }
  .sa-work-progress {
    margin: 22px auto 0;
    max-width: 460px;
    text-align: left;
  }
  .sa-work-progress-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
  }
  .sa-work-progress-row .lbl {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .sa-work-progress-row .pct {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.04em;
    color: var(--activemenu, #3d91e4);
    font-variant-numeric: tabular-nums;
  }
  .sa-work-bar {
    height: 8px;
    border-radius: 99px;
    background: color-mix(in srgb, var(--activemenu, #3d91e4) 12%, #e2e8f0);
    overflow: hidden;
    position: relative;
  }
  .sa-work-bar-fill {
    height: 100%;
    border-radius: 99px;
    background: linear-gradient(90deg, var(--activemenu, #3d91e4), #38bdf8);
    transition: width 0.5s ease;
  }
  .sa-work-note {
    margin-top: 22px;
    font-size: 12.5px;
    color: #94a3b8;
  }
`;

function pipelineIndex(progress) {
  const stage = String(progress?.stage || "").toLowerCase();
  const pct = Number(progress?.progress);
  const n = Number.isFinite(pct) ? pct : 0;
  if (/complete|result|dashboard|review$/.test(stage) || n >= 90) return 3;
  if (/analy|scan|finding|reviewer/.test(stage) || n >= 55) return 2;
  if (/index|parse|detect|discover/.test(stage) || n >= 25) return 1;
  return 0;
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isAllowedArchive(file) {
  const name = (file?.name || "").toLowerCase();
  return zipTypeExtensions.some((ext) => name.endsWith(`.${ext}`));
}

export function repoLabelFromUrl(url) {
  const m = String(url || "").match(/github\.com\/([^/]+)\/([^/#?]+)/i);
  if (!m) return "";
  return `${m[1]}/${m[2].replace(/\.git$/i, "")}`;
}

function useImportCss() {
  useEffect(() => {
    const id = "sa-import-application-styles";
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement("style");
      tag.id = id;
      document.head.appendChild(tag);
    }
    tag.textContent = IMPORT_CSS;
  }, []);
}

export function ImportWorkingView({ progress, repoLabel, kicker = "Analyzing your application" }) {
  useImportCss();
  const active = pipelineIndex(progress);
  const lines = React.useMemo(() => {
    const base = STATUS_LINES[active] || STATUS_LINES[0];
    if (!progress?.stage) return base;
    return [{ title: progress.stage, sub: progress.stageDetail || base[0].sub }, ...base];
  }, [active, progress?.stage, progress?.stageDetail]);
  const logs = ACTIVITY_LOGS[active] || ACTIVITY_LOGS[0];
  const [idx, setIdx] = useState(0);
  const [logIdx, setLogIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const [logVisible, setLogVisible] = useState(true);
  const [dots, setDots] = useState("");

  useEffect(() => {
    setIdx(0);
    setLogIdx(0);
    setVisible(true);
    setLogVisible(true);
  }, [active]);

  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d.length >= 3 ? "" : `${d}.`)), 420);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let fade;
    const cycle = setInterval(() => {
      setVisible(false);
      fade = setTimeout(() => {
        setIdx((i) => i + 1);
        setVisible(true);
      }, 280);
    }, 2600);
    return () => {
      clearInterval(cycle);
      clearTimeout(fade);
    };
  }, [active, lines.length]);

  useEffect(() => {
    let fade;
    const cycle = setInterval(() => {
      setLogVisible(false);
      fade = setTimeout(() => {
        setLogIdx((i) => i + 1);
        setLogVisible(true);
      }, 180);
    }, 1600);
    return () => {
      clearInterval(cycle);
      clearTimeout(fade);
    };
  }, [active, logs.length]);

  const current = lines[idx % lines.length] || lines[0];
  const logLine = logs[logIdx % logs.length];
  const reported = Number(progress?.progress);
  const pct = Number.isFinite(reported) ? Math.min(100, Math.max(0, reported)) : 0;

  return (
    <div className="sa-work" aria-live="polite">
      <div className="sa-work-scene" aria-hidden="true">
        <div className="sa-work-glow" />
        <div className="sa-work-orbit">
          <span className="sa-work-pebble" />
        </div>
        <div className="sa-work-orbit rev">
          <span className="sa-work-pebble" />
        </div>
        <span className="sa-work-ring r1" />
        <span className="sa-work-ring r2" />
        <span className="sa-work-ring r3" />
        <div className="sa-work-core">
          <i className={CORE_ICONS[active] || CORE_ICONS[0]} />
        </div>
        <div className="sa-work-scan" />
      </div>
      <div className="sa-work-kicker">{kicker}</div>
      <div className={`sa-work-copy ${visible ? "is-in" : "is-out"}`}>
        <h2 className="sa-work-title">
          {current.title}
          {dots}
        </h2>
        <p className="sa-work-sub">{current.sub}</p>
      </div>
      {repoLabel ? (
        <div className="sa-work-repo">
          {/\.(zip|tar|tgz|gz|rar)$/i.test(repoLabel) ? <FaCloudUploadAlt /> : <FaGithub />}
          {repoLabel}
        </div>
      ) : null}
      <div className="sa-work-progress">
        <div className="sa-work-progress-row">
          <span className="lbl">{current.title || "Working"}</span>
          <span className="pct">{Math.round(pct)}%</span>
        </div>
        <div className="sa-work-bar" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
          <div className="sa-work-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className={`sa-work-log ${logVisible ? "is-in" : "is-out"}`}>
        <em />
        {logLine}
      </div>
      <div className="sa-work-note">Usually a few minutes. You can leave this tab open.</div>
    </div>
  );
}

const ImportApplicationSource = ({
  onContinue,
  submitting = false,
  hideTitle = false,
}) => {
  const [source, setSource] = useState(IMPORT_SOURCES.GITHUB);
  const [step, setStep] = useState(1);
  const [repoUrl, setRepoUrl] = useState("");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  useImportCss();
  const selectedCard = SOURCE_CARDS.find((card) => card.id === source) || SOURCE_CARDS[0];
  const needsUrl = source !== IMPORT_SOURCES.UPLOAD;
  const isComingSoon = Boolean(selectedCard.comingSoon);
  const availableCards = SOURCE_CARDS.filter((card) => !card.comingSoon);
  const soonCards = SOURCE_CARDS.filter((card) => card.comingSoon);
  const leadText =
    step === 1
      ? "Choose a source, then continue. We'll clone or unpack it, map the architecture, and open insights when ready."
      : isComingSoon
        ? `${selectedCard.title} import is not available yet.`
        : needsUrl
          ? "Enter the repository URL to clone and analyze this codebase."
          : "Upload a ZIP or TAR of the codebase to start analysis.";

  const renderCard = (card) => {
    const Icon = card.icon;
    const selected = source === card.id;
    return (
      <div
        key={card.id}
        className={`sa-import-card${selected ? " is-selected" : ""}${card.comingSoon ? " is-soon" : ""}`}
        onClick={() => setSource(card.id)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setSource(card.id);
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={selected}
      >
        <div className="sa-import-card-top">
          <span className="sa-import-card-icon">
            <Icon />
          </span>
          <div className="sa-import-card-copy">
            <h3>{card.title}</h3>
            <p>{card.description}</p>
            {card.comingSoon ? <span className="sa-import-soon-badge">Coming soon</span> : null}
          </div>
          <span className="sa-import-card-check" aria-hidden="true">
            {selected ? <FaCheck /> : null}
          </span>
        </div>
        {!card.comingSoon ? (
          <ul>
            {card.bullets.map((b) => (
              <li key={b}>
                <i className="fa-solid fa-check" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  };

  const handlePickFile = (picked) => {
    if (!picked) return;
    if (!isAllowedArchive(picked)) {
      showNotificationGlobal(
        `Please upload a valid archive (${zipTypeExtensions.join(", ").toUpperCase()}).`,
        "error",
        "topCenter"
      );
      return;
    }
    setSource(IMPORT_SOURCES.UPLOAD);
    setFile(picked);
  };

  const handleSubmit = async () => {
    if (isComingSoon) {
      showNotificationGlobal(`${selectedCard.title} is coming soon.`, "info", "topCenter");
      return;
    }
    if (needsUrl) {
      const url = repoUrl.trim();
      if (!url) {
        showNotificationGlobal(`Please enter a ${selectedCard.urlLabel || "repository URL"}.`, "error", "topCenter");
        return;
      }
      if (selectedCard.urlPattern && !selectedCard.urlPattern.test(url)) {
        showNotificationGlobal(
          `Enter a valid ${selectedCard.title} URL${selectedCard.urlExample ? ` (e.g. ${selectedCard.urlExample})` : "."}`,
          "error",
          "topCenter"
        );
        return;
      }
      await onContinue?.({
        source,
        githubUrl: url,
        repoUrl: url,
        file: null,
      });
      return;
    }

    if (!file) {
      showNotificationGlobal("Please upload a code archive.", "error", "topCenter");
      return;
    }
    await onContinue?.({
      source: IMPORT_SOURCES.UPLOAD,
      githubUrl: null,
      file,
    });
  };

  return (
    <div className="sa-import-wrap">
      {!hideTitle ? (
        <h2 className="sa-import-title">Let&apos;s import your existing application</h2>
      ) : null}
      {step === 1 ? (
        <div className="sa-import-toolbar">
          <p className="sa-import-lead">{leadText}</p>
          <div className="sa-import-footer-actions">
            <button
              type="button"
              className="sa-import-next"
              onClick={() => setStep(2)}
              disabled={submitting}
            >
              Next <FaArrowRight />
            </button>
          </div>
        </div>
      ) : null}

      {step === 1 ? (
        <>
          <div className="sa-import-section-label">Available now</div>
          <div className="sa-import-cards is-ready">{availableCards.map(renderCard)}</div>
          {soonCards.length ? (
            <div className="sa-import-soon-group">
              <div className="sa-import-section-label">Coming soon</div>
              <div className="sa-import-cards">{soonCards.map(renderCard)}</div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="sa-import-stage">
          <aside className="sa-import-stage-aside">
            <div className="sa-import-aside-head">
              <h3>What happens next</h3>
              <span className="sa-import-aside-badge">Read-only</span>
            </div>
            <p>Xray clones or unpacks the code, then opens insights when analysis is ready.</p>
            <ul className="sa-import-steps">
              <li className="is-next">
                <span className="sa-import-step-mark d-flex" aria-hidden="true">
                  <i className="fa-solid fa-code-branch" />
                </span>
                <div className="sa-import-step-body">
                  <strong>Bring the code in</strong>
                  <span>Clone the repo or unpack the archive. Nothing is trained on.</span>
                </div>
              </li>
              <li>
                <span className="sa-import-step-mark d-flex" aria-hidden="true">
                  <i className="fa-solid fa-layer-group" />
                </span>
                <div className="sa-import-step-body">
                  <strong>Map the architecture</strong>
                  <span>Identify modules, languages, and how the app is structured.</span>
                </div>
              </li>
              <li>
                <span className="sa-import-step-mark d-flex" aria-hidden="true">
                  <i className="fa-solid fa-shield-halved" />
                </span>
                <div className="sa-import-step-body">
                  <strong>Review and score</strong>
                  <span>Run findings, health scoring, and dependency analysis.</span>
                </div>
              </li>
              <li>
                <span className="sa-import-step-mark d-flex" aria-hidden="true">
                  <i className="fa-solid fa-chart-line" />
                </span>
                <div className="sa-import-step-body">
                  <strong>Open Xray insights</strong>
                  <span>Explore overview, files, and findings when the job completes.</span>
                </div>
              </li>
            </ul>
          </aside>
          <section key={source} className="sa-import-stage-main">
            <h3 className="sa-import-stage-title">
              {needsUrl ? "Connect your repository" : "Upload your archive"}
            </h3>
            <p className="sa-import-stage-copy">{leadText}</p>
            <div className="sa-import-source-switch">
              {availableCards.map((card) => {
                const Icon = card.icon;
                return (
                  <button
                    key={card.id}
                    type="button"
                    className={`sa-import-source-tab${source === card.id ? " is-active" : ""}`}
                    onClick={() => setSource(card.id)}
                    disabled={submitting}
                  >
                    {Icon ? <Icon /> : null}
                    {card.title.replace(" repository", "")}
                  </button>
                );
              })}
              <button
                type="button"
                className="sa-import-source-more"
                onClick={() => setStep(1)}
                disabled={submitting}
              >
                All sources
              </button>
            </div>
            {isComingSoon ? (
              <div className="sa-import-panel sa-import-soon">
                <h3>Coming soon</h3>
                <p>
                  {selectedCard.title} import is not available yet. Use a GitHub repository or upload an archive to start analysis now.
                </p>
              </div>
            ) : needsUrl ? (
              <div className="sa-import-panel">
                <div className="sa-import-field">
                  <label htmlFor="sa-repo-url">
                    {selectedCard.urlLabel || "Repository URL"} <span className="req">*</span>
                  </label>
                  <div className="sa-import-url-row">
                    <input
                      id="sa-repo-url"
                      type={source === IMPORT_SOURCES.S3 ? "text" : "url"}
                      placeholder={selectedCard.urlPlaceholder || "https://"}
                      value={repoUrl}
                      onChange={(e) => setRepoUrl(e.target.value)}
                      disabled={submitting}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSubmit();
                      }}
                    />
                    <button
                      type="button"
                      className="sa-import-go"
                      onClick={handleSubmit}
                      disabled={submitting || !repoUrl.trim()}
                    >
                      Analyze <FaArrowRight />
                    </button>
                  </div>
                  {selectedCard.urlExample ? (
                    <div className="sa-import-hint">Example: {selectedCard.urlExample}</div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="sa-import-panel">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip,.tar,.gz,.rar,.tar.gz"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    handlePickFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <div
                  className={`sa-import-drop${dragOver ? " is-dragover" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    handlePickFile(e.dataTransfer.files?.[0]);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
                  }}
                >
                  <div className="icon">
                    <FaCloudUploadAlt />
                  </div>
                  <div className="sa-import-drop-title">
                    Drop archive here or browse
                  </div>
                  <div className="sa-import-drop-sub">
                    ZIP, TAR, TAR.GZ, RAR supported
                  </div>
                </div>
                {file ? (
                  <div className="sa-import-ready">
                    <div>
                      <strong>{file.name}</strong>
                      <span>{formatBytes(file.size)}</span>
                    </div>
                    <div className="sa-import-ready-actions">
                      <button
                        type="button"
                        className="sa-import-text-btn"
                        onClick={() => setFile(null)}
                        disabled={submitting}
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        className="sa-import-go-wide"
                        onClick={handleSubmit}
                        disabled={submitting}
                      >
                        Analyze <FaArrowRight />
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
            <div className="sa-import-footer">
              <div className="sa-import-secure">
                <i className="fa-solid fa-shield-halved" />
                <span>Your code stays private. We never use it for training.</span>
              </div>
            </div>
          </section>
        </div>
      )}

      {step === 1 ? (
        <div className="sa-import-footer">
          <div className="sa-import-secure">
            <i className="fa-solid fa-shield-halved" />
            <span>Your code stays private. We never use it for training.</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export { IMPORT_SOURCES };
export default ImportApplicationSource;
